// src/admin/pages/AdminTopBanner.js
import React, { useState, useEffect, useRef, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
  FaTrash,
  FaSave,
  FaCheck,
  FaBullhorn,
  FaPen,
  FaPalette,
} from "react-icons/fa";
import {
  getAdminTopBanners,
  createAdminTopBanner,
  updateAdminTopBanner,
  deleteAdminTopBanner,
  getAdminTopBannerSettings,
  updateAdminTopBannerSettings,
} from "../../services/adminTopBanner.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

function AdminTopBanner() {
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);

  const [bannerColor, setBannerColor] = useState("#1E88E5");
const [savedBannerColor, setSavedBannerColor] = useState("#1E88E5");
const [savingColor, setSavingColor] = useState(false);
const [colorModalOpen, setColorModalOpen] = useState(false);

  const newRowRef = useRef(null);

  const TEXT = {
    EN: {
      title: "Top Banner Messages",
      add: "Add",
      loading: "Loading banners...",
      placeholderEn: "English message",
      placeholderAr: "Arabic message",
      save: "Save",
      delete: "Delete",
      edit: "Edit",
      confirmSave: "Save changes?",
      confirmDelete: "Delete banner?",
      confirmText: "This action cannot be undone.",
      cancel: "Cancel",
        english: "English",
        hexColor: "Hex color code",
invalidHex: "Enter a valid hex color, example: #1E88E5",
  arabic: "Arabic",
      confirm: "Confirm",
      saved: "Banner saved successfully",
      deleted: "Banner deleted successfully",
      english: "English",
      arabic: "Arabic",
      bannerStyle: "Banner style",
backgroundColor: "Background color",
changeColor: "Change color",
colorModalTitle: "Change top banner color",
colorModalDesc:
  "Choose the background color used for the customer-facing top banner.",
saveColor: "Done",
colorSaved: "Banner color saved successfully",
preview: "Preview",
      actions: "Actions",
       noData: "No banner messages yet",
    },
    AR: {
      title: "رسائل الشريط العلوي",
      add: "إضافة",
      loading: "جارٍ تحميل البنرات...",
        english: "الإنجليزية",
  arabic: "العربية",
      placeholderEn: "النص بالإنجليزية",
      placeholderAr: "النص بالعربية",
      save: "حفظ",
      hexColor: "كود اللون",
invalidHex: "أدخل كود لون صحيح مثل: #1E88E5",
      delete: "حذف",
      edit: "تعديل",
      bannerStyle: "تصميم الشريط",
backgroundColor: "لون الخلفية",
changeColor: "تغيير اللون",
colorModalTitle: "تغيير لون الشريط العلوي",
colorModalDesc:
  "اختر لون الخلفية الذي سيظهر في الشريط العلوي في واجهة العميل.",
saveColor: "تم",
colorSaved: "تم حفظ لون الشريط بنجاح",
preview: "معاينة",
      confirmSave: "حفظ التعديلات؟",
      confirmDelete: "حذف الرسالة؟",
      confirmText: "لا يمكن التراجع عن هذا الإجراء.",
      cancel: "إلغاء",
      confirm: "تأكيد",
      saved: "تم حفظ الرسالة بنجاح",
      deleted: "تم حذف الرسالة بنجاح",
        english: "الإنجليزية",
  arabic: "العربية",
      actions: "إجراءات",
      noData: "لا توجد بيانات",
       noData: "لا توجد رسائل حالياً",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
  const isAdmin = adminUser.role === "admin";
  const canAccess = isAdmin || perms.can_manage_banners;

  const load = () => {
    setLoading(true);
    getAdminTopBanners()
      .then(setRows)
      .finally(() => setLoading(false));
  };

  const loadSettings = () => {
  getAdminTopBannerSettings()
    .then((settings) => {
      const color = settings?.background_color || "#1E88E5";
      setBannerColor(color);
      setSavedBannerColor(color);
    })
    .catch(() => {});
};

const normalizeHexColor = (value) => {
  const cleaned = String(value || "").trim();

  if (!cleaned) return "";

  return cleaned.startsWith("#") ? cleaned : `#${cleaned}`;
};

const isValidHexColor = (value) => {
  return /^#([0-9A-Fa-f]{6})$/.test(normalizeHexColor(value));
};

const saveBannerColor = async () => {
  const normalizedColor = normalizeHexColor(bannerColor);

  if (!isValidHexColor(normalizedColor)) return;

  setSavingColor(true);

  try {
    const settings = await updateAdminTopBannerSettings({
      background_color: normalizedColor,
    });

    const color = settings?.background_color || normalizedColor;

    setBannerColor(color);
    setSavedBannerColor(color);
    setColorModalOpen(false);
    setToast(TEXT.colorSaved);
  } finally {
    setSavingColor(false);
  }
};

  useEffect(load, []);

  useEffect(loadSettings, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleAdd = async () => {
  const newRow = await createAdminTopBanner({
    message_en: "",
    message_ar: "",
    sort_order: rows.length + 1,
  });

  setRows((prev) => [newRow, ...prev]);
  setEditingRowId(newRow.id);

  // 🔥 reliable scroll AFTER render
  requestAnimationFrame(() => {
    setTimeout(() => {
      newRowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  });
};

  const confirmSave = async (row) => {
    await updateAdminTopBanner(row.id, row);
    setToast(TEXT.saved);
    setConfirm(null);
    setEditingRowId(null);
    load();
  };

  const confirmDelete = async (id) => {
    await deleteAdminTopBanner(id);
    setToast(TEXT.deleted);
    setConfirm(null);
    if (editingRowId === id) setEditingRowId(null);
    load();
  };

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6">
          {!canAccess && (
            <div className="bg-red-50 border border-red-200 rounded-[14px] p-6 text-center shadow-sm">
              <p className="text-red-700 font-semibold">
                {lang === "AR"
                  ? "ليس لديك صلاحية لإدارة البنرات"
                  : "You do not have permission to manage banners."}
              </p>
            </div>
          )}

          {canAccess && (
            <>
              {/* HEADER */}
              <div
  dir={lang === "AR" ? "rtl" : "ltr"}
  className="flex items-center justify-between gap-3 mb-6"
>
                {/* TITLE */}
                <h1
                  className={`text-[20px] font-semibold text-gray-900 flex items-center gap-2 truncate ${
                    lang === "AR" ? "text-right" : ""
                  }`}
                >
                  <FaBullhorn className="text-gray-900 text-[18px]" />
                  <span>{TEXT.title}</span>
                </h1>
 
               <div className="flex items-center gap-2 shrink-0">
  {/* COLOR BUTTON */}
  <button
    type="button"
    onClick={() => setColorModalOpen(true)}
    className="
      flex
      h-8 sm:h-9 px-3
      bg-white text-gray-900
      border border-[#d9d9d9]
      rounded-lg
      text-xs sm:text-sm font-medium
      items-center gap-2
      hover:bg-[#f6f6f7]
      transition
      shadow-sm
    "
    title={TEXT.changeColor}
  >
    <span
      className="w-4 h-4 rounded-full border border-gray-300 shadow-inner"
      style={{ backgroundColor: savedBannerColor }}
    />
    <span className="hidden sm:inline">{TEXT.changeColor}</span>
  </button>

  {/* ADD BUTTON */}
  <button
    onClick={handleAdd}
    disabled={loading}
    className="
      flex
      h-8 sm:h-9 px-3 sm:px-4 text-xs sm:text-sm
      bg-black text-white
      rounded-lg
      font-medium
      items-center gap-2
      hover:bg-gray-800
      transition
      disabled:opacity-50
      disabled:cursor-not-allowed
    "
  >
    {TEXT.add}
  </button>
</div>
              </div>

              {/* TABLE */}
              <div className="bg-white border border-[#e3e3e3] rounded-[14px] shadow-[0_12px_32px_rgba(0,0,0,0.10)] overflow-hidden">
                {/* TOP BAR */}
                <div className={`flex items-center justify-between px-4 py-3 border-b border-[#e3e3e3] ${lang === "AR" ? "flex-row-reverse" : ""}`}>
                  <span className="text-sm text-gray-500">
  {loading ? (
    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
  ) : (
    `${rows.length} ${lang === "AR" ? "رسالة" : "messages"}`
  )}
</span>
                </div>

                <div className="overflow-x-auto scrollbar-thin">
                 <div className="hidden md:block">
                  <table
                        dir={lang === "AR" ? "rtl" : "ltr"}
                        className="w-full text-left"
                      >
                    <thead className="bg-[#f6f6f7] text-[#6d7175] text-[13px]">
                      <tr>
                        <th className="px-4 py-3 font-medium">{TEXT.english}</th>
                        <th className="px-4 py-3 font-medium">{TEXT.arabic}</th>
                        <th className={`px-4 py-3 font-medium ${lang === "AR" ? "text-left" : "text-center"}`}>{TEXT.actions}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
  [...Array(4)].map((_, i) => (
    <tr key={i} className="animate-pulse">
      <td className="px-4 py-3">
        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="h-8 bg-gray-200 rounded w-20 mx-auto"></div>
      </td>
    </tr>
  ))
) : rows.length === 0 ? (
                       <tr>
  <td colSpan="3" className="py-10 text-center">
    <div className="flex flex-col items-center gap-2">
      <FaBullhorn className="text-gray-300 text-2xl" />
      
      <p className="text-sm font-medium text-gray-900">
        {TEXT.noData}
      </p>

      <button
        onClick={handleAdd}
  disabled={loading}
        className="mt-2 text-sm text-blue-600 hover:underline"
      >
        {TEXT.add}
      </button>
    </div>
  </td>
</tr>
                      ) : (
                        rows.map((row, i) => {
                          const isEditing = editingRowId === row.id;

                          return (
                            <tr
  key={row.id}
  ref={i === 0 ? newRowRef : null}
  className={`
    border-b border-[#ececec] transition
    hover:bg-[#f6f6f7]
    ${isEditing ? "bg-blue-50" : ""}
  `}
>
                              <td className="px-4 py-3 align-middle">
                                {isEditing ? (
                                  <textarea
  rows={2}
  className="w-full min-h-[72px] p-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-500"
  value={row.message_en}
  placeholder={TEXT.placeholderEn}
  onChange={(e) =>
    setRows((r) =>
      r.map((x) =>
        x.id === row.id
          ? { ...x, message_en: e.target.value }
          : x
      )
    )
  }
/>
                                ) : (
                                  <div className="text-[13px] text-gray-900 whitespace-pre-wrap break-words">
                                    {row.message_en || "—"}
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-3 align-middle">
                                {isEditing ? (
                                  <textarea
  rows={2}
  dir="rtl"
  className="w-full min-h-[72px] p-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-500"
  value={row.message_ar}
  placeholder={TEXT.placeholderAr}
  onChange={(e) =>
    setRows((r) =>
      r.map((x) =>
        x.id === row.id
          ? { ...x, message_ar: e.target.value }
          : x
      )
    )
  }
/>
                                ) : (
                                  <div className="text-[13px] text-gray-900 whitespace-pre-wrap break-words">
                                    {row.message_ar || "—"}
                                  </div>
                                )}
                              </td>

                              <td className="px-4 py-3 align-middle text-center">
  <div className="flex items-center justify-center gap-2">

    {isEditing ? (
      <>
        {/* SAVE (PRIMARY) */}
        <button
          onClick={() => setConfirm({ type: "save", row })}
          className="
            flex items-center gap-1
            px-3 py-1.5
            text-[12px] font-medium
            rounded-md
            bg-black text-white
            hover:bg-gray-800
            transition
          "
        >
          <FaSave size={12} />
          {TEXT.save}
        </button>

        {/* CANCEL */}
        <button
          onClick={async () => {
  const row = rows.find(r => r.id === editingRowId);

  if (row && !row.message_en?.trim() && !row.message_ar?.trim()) {
    // 🔥 delete empty row
    await deleteAdminTopBanner(row.id);
    setRows(prev => prev.filter(r => r.id !== row.id));
  }

  setEditingRowId(null);
}}
          className="
            px-3 py-1.5
            text-[12px]
            rounded-md
            border border-gray-300
            text-gray-900
            hover:bg-gray-100
            transition
          "
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>
      </>
    ) : (
      <>
        {/* EDIT */}
        <button
          onClick={() => setEditingRowId(row.id)}
          className="
            w-9 h-9
            flex items-center justify-center
            rounded-md
            border border-gray-300
            text-gray-900
            hover:bg-gray-100
            hover:scale-105
            transition
          "
          title={TEXT.edit}
        >
          <FaPen size={12} />
        </button>

        {/* DELETE */}
        <button
          onClick={() => setConfirm({ type: "delete", row })}
          className="
            w-9 h-9
            flex items-center justify-center
            rounded-md
            border border-red-200
            text-red-600
            hover:bg-red-50
            hover:scale-105
            transition
          "
          title={TEXT.delete}
        >
          <FaTrash size={13} />
        </button>
      </>
    )}
  </div>
</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                 </div> 
                </div>
                {/* MOBILE VIEW */}
<div className="md:hidden space-y-3 mt-4 pb-20">
  {loading ? (
  [...Array(3)].map((_, i) => (
    <div key={i} className="animate-pulse bg-white p-4 rounded-xl border space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>

      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>

      <div className="flex justify-end gap-2 pt-2">
        <div className="h-8 w-16 bg-gray-200 rounded"></div>
        <div className="h-8 w-16 bg-gray-200 rounded"></div>
      </div>
    </div>
  ))
) : rows.length === 0 ? (
    <div className="text-center py-10 text-gray-500 flex flex-col items-center gap-2">
  <FaBullhorn className="text-3xl text-gray-300" />

  <p className="text-sm font-medium text-gray-900">
    {TEXT.noData}
  </p>

  <button
    onClick={handleAdd}
  disabled={loading}
    className="text-sm text-blue-600 hover:underline"
  >
    {TEXT.add}
  </button>
</div>
  ) : (
    rows.map((row) => {
      const isEditing = editingRowId === row.id;

      return (
        <div
          key={row.id}
          className={`
            bg-white rounded-xl shadow
            border border-[#e8e8e8]
            p-4 space-y-3
            ${isEditing ? "bg-blue-50" : ""}
          `}
        >
          {/* ENGLISH */}
          <div>
            <div className="text-[11px] text-gray-400 mb-1">
              {TEXT.english}
            </div>

            {isEditing ? (
              <textarea
  rows={2}
  className="w-full min-h-[72px] p-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-500"
  value={row.message_en}
  placeholder={TEXT.placeholderEn}
  onChange={(e) =>
    setRows((r) =>
      r.map((x) =>
        x.id === row.id
          ? { ...x, message_en: e.target.value }
          : x
      )
    )
  }
/>
            ) : (
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
  {row.message_en || "—"}
</div>
            )}
          </div>

          {/* ARABIC */}
          <div>
            <div className="text-[11px] text-gray-400 mb-1">
              {TEXT.arabic}
            </div>

            {isEditing ? (
              <textarea
  rows={2}
  dir="rtl"
  className="w-full min-h-[72px] p-2 border border-gray-300 rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-500"
  value={row.message_ar}
  placeholder={TEXT.placeholderAr}
  onChange={(e) =>
    setRows((r) =>
      r.map((x) =>
        x.id === row.id
          ? { ...x, message_ar: e.target.value }
          : x
      )
    )
  }
/>
            ) : (
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
  {row.message_ar || "—"}
</div>
            )}
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            {isEditing ? (
              <>
                <button
                  onClick={() => setConfirm({ type: "save", row })}
                  className="px-3 py-1.5 text-sm rounded-md bg-black text-white"
                >
                  {TEXT.save}
                </button>

                <button
                  onClick={async () => {
  const row = rows.find(r => r.id === editingRowId);

  if (row && !row.message_en?.trim() && !row.message_ar?.trim()) {
    // 🔥 delete empty row
    await deleteAdminTopBanner(row.id);
    setRows(prev => prev.filter(r => r.id !== row.id));
  }

  setEditingRowId(null);
}}
                  className="px-3 py-1.5 text-sm border rounded-md"
                >
                  {TEXT.cancel}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditingRowId(row.id)}
                  className="px-3 py-1.5 text-sm border rounded-md"
                >
                  {TEXT.edit}
                </button>

                <button
                  onClick={() => setConfirm({ type: "delete", row })}
                  className="px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-md"
                >
                  {TEXT.delete}
                </button>
              </>
            )}
          </div>
        </div>
      );
    })
  )}
  
</div>
              </div>
            </>
          )}
        </main>
      </div>

{/* COLOR MODAL */}
{colorModalOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="bg-white rounded-[16px] w-full max-w-md shadow-2xl overflow-hidden border border-[#e3e3e3]"
    >
      {/* MODAL HEADER */}
      <div className="px-5 py-4 border-b border-[#e3e3e3]">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border border-gray-200 shadow-sm shrink-0"
            style={{ backgroundColor: bannerColor }}
          >
            <FaPalette className="text-white drop-shadow" />
          </div>

          <div>
            <h2 className="text-[16px] font-semibold text-gray-900">
              {TEXT.colorModalTitle}
            </h2>

            <p className="text-sm text-gray-500 mt-1 leading-5">
              {TEXT.colorModalDesc}
            </p>
          </div>
        </div>
      </div>

      {/* MODAL BODY */}
<div className="p-5">
  <div>
    <label className="block text-sm font-medium text-gray-900 mb-2">
      {TEXT.hexColor}
    </label>

    <div className="flex items-center gap-3">
      <label className="relative w-14 h-10 rounded-lg border border-gray-300 overflow-hidden cursor-pointer shadow-inner bg-white shrink-0">
        <input
          type="color"
          value={isValidHexColor(bannerColor) ? normalizeHexColor(bannerColor) : savedBannerColor}
          onChange={(e) => setBannerColor(e.target.value)}
          className="absolute inset-0 w-full h-full scale-150 cursor-pointer"
        />
      </label>

      <div className="flex-1">
        <input
          value={bannerColor}
          onChange={(e) => setBannerColor(e.target.value)}
          onBlur={() => {
            if (isValidHexColor(bannerColor)) {
              setBannerColor(normalizeHexColor(bannerColor));
            }
          }}
          className={`
            w-full h-10 px-3
            border
            rounded-lg
            text-sm font-mono text-gray-800
            focus:outline-none
            focus:ring-2
            transition
            ${
              bannerColor && !isValidHexColor(bannerColor)
                ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100"
                : "border-gray-300 focus:border-gray-500 focus:ring-gray-900/10"
            }
          `}
          placeholder="#1E88E5"
        />

        {bannerColor && !isValidHexColor(bannerColor) && (
          <p className="text-xs text-red-600 mt-1">
            {TEXT.invalidHex}
          </p>
        )}
      </div>
    </div>
  </div>
</div>

      {/* MODAL FOOTER */}
      <div className="px-5 py-4 border-t border-[#e3e3e3] bg-[#f6f6f7] flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            setBannerColor(savedBannerColor);
            setColorModalOpen(false);
          }}
          disabled={savingColor}
          className="
            h-9 px-4 rounded-lg
            border border-gray-300
            bg-white text-gray-900
            text-sm font-medium
            hover:bg-gray-50
            transition
            disabled:opacity-50
          "
        >
          {TEXT.cancel}
        </button>

        <button
  type="button"
  onClick={saveBannerColor}
  disabled={
    savingColor ||
    !isValidHexColor(bannerColor) ||
    normalizeHexColor(bannerColor).toLowerCase() === savedBannerColor.toLowerCase()
  }
  className={`
    h-9 px-4 rounded-lg
    text-sm font-medium
    transition
    ${
      savingColor ||
      !isValidHexColor(bannerColor) ||
      normalizeHexColor(bannerColor).toLowerCase() === savedBannerColor.toLowerCase()
        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
        : "bg-black text-white hover:bg-gray-800"
    }
  `}
>
  {savingColor ? TEXT.loading : TEXT.saveColor}
</button>
      </div>
    </div>
  </div>
)}

      {/* CONFIRM MODAL */}
      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-[14px] p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-[16px] font-semibold text-gray-900 mb-3">
              {confirm.type === "delete" ? TEXT.confirmDelete : TEXT.confirmSave}
            </h2>

            <p className="text-sm text-gray-500 mb-5">
              {TEXT.confirmText}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm"
              >
                {TEXT.cancel}
              </button>

              <button
                onClick={() =>
                  confirm.type === "delete"
                    ? confirmDelete(confirm.row.id)
                    : confirmSave(confirm.row)
                }
                className={`px-4 py-2 rounded-md text-white text-sm ${
                  confirm.type === "delete"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-black hover:bg-gray-800"
                }`}
              >
                {TEXT.confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
{toast && (
  <div
    dir={lang === "AR" ? "rtl" : "ltr"}
    className={`fixed bottom-6 ${lang === "AR" ? "left-6" : "right-6"} z-50`}
  >
    <div
      className="
        min-w-[260px] max-w-[360px]
        bg-white
        border border-[#d9d9d9]
        rounded-[14px]
        shadow-[0_12px_32px_rgba(0,0,0,0.18)]
        px-4 py-3
        flex items-start gap-3
      "
    >
      <div className="w-6 h-6 rounded-full bg-[#008060] flex items-center justify-center shrink-0 mt-0.5">
        <FaCheck className="text-white text-[11px]" />
      </div>

      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 leading-5">
          {toast}
        </p>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

export default AdminTopBanner;